import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, MessageSquare, Send, RefreshCw, X } from 'lucide-react';

const formatBotText = (text) => {
  if (!text) return '';
  return text.replace(/\*\*(.+?)\*\*/g, '$1');
};

const AutoDevisStaffBotChat = ({
  api,
  mode = 'workspace',
  conversationId,
  clientName = '',
  clientMessage = '',
  clientTel = '',
  clientEmail = '',
  onClose,
  onDevisCreated,
  compact = false,
}) => {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  // Fiche client — obligatoire pour valider un devis (nom + téléphone + e-mail)
  const [ident, setIdent] = useState({ name: clientName || '', tel: clientTel || '', email: clientEmail || '' });
  useEffect(() => { setIdent({ name: clientName || '', tel: clientTel || '', email: clientEmail || '' }); }, [clientName, clientTel, clientEmail]);
  const bottomRef = useRef(null);
  const isWorkspace = mode === 'workspace';

  const load = useCallback(async () => {
    if (!api) return;
    if (!isWorkspace && !conversationId) return;
    setLoading(true);
    try {
      if (isWorkspace) {
        const res = await api.get('/chat-ai-devis/staff-bot/workspace');
        setThread(res.data.thread || null);
        setMessages(res.data.messages || []);
        setInbox(res.data.inbox || []);
      } else {
        const res = await api.get('/chat-ai-devis/staff-bot/thread', {
          params: { conversation_id: conversationId },
        });
        setThread(res.data.thread || null);
        setMessages(res.data.messages || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, conversationId, isWorkspace]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    if (!isWorkspace && !conversationId) return;
    setSending(true);
    try {
      let res;
      if (isWorkspace) {
        res = await api.post('/chat-ai-devis/staff-bot/workspace/messages', {
          content: text,
          client_name: ident.name.trim(),
          client_tel: ident.tel.trim(),
          client_email: ident.email.trim(),
        });
      } else {
        res = await api.post('/chat-ai-devis/staff-bot/messages', {
          conversation_id: conversationId,
          content: text,
          client_message: clientMessage,
          client_name: ident.name.trim() || clientName,
          client_tel: ident.tel.trim() || clientTel,
          client_email: ident.email.trim() || clientEmail,
        });
      }
      setDraft('');
      if (res.data.staff_message) {
        setMessages((prev) => [...prev, res.data.staff_message]);
      }
      if (res.data.bot_message) {
        setMessages((prev) => [...prev, res.data.bot_message]);
      }
      if (res.data.thread) {
        setThread(res.data.thread);
      } else if (res.data.thread_id && !thread) {
        setThread({ id: res.data.thread_id, client_conversation_id: conversationId });
      }
      if (res.data.devis_created && onDevisCreated) {
        onDevisCreated(res.data);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur envoi message bot');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const subtitle = isWorkspace
    ? 'Créez un devis ou posez une question'
    : `${clientName || 'Client'} · aide chiffrage`;

  return (
    <div
      className={`flex flex-col border border-purple-500/25 bg-purple-500/5 rounded-2xl overflow-hidden ${
        compact ? 'h-[420px]' : 'h-[min(70vh,640px)] xl:sticky xl:top-4'
      }`}
      data-testid="autodevis-staff-bot-chat"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-purple-500/20 bg-black/20">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-300">
            <Bot className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">Bot Auto-Devis</p>
            <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/10"
            title="Actualiser"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 xl:hidden"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {isWorkspace && inbox.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2 space-y-1.5" data-testid="autodevis-cadbot-inbox">
            <p className="text-[10px] uppercase tracking-wide text-amber-300 font-semibold">
              Devis CADBOT à accepter ({inbox.reduce((n, i) => n + (i.devis_count || 0), 0)})
            </p>
            {inbox.slice(0, 8).map((item) => (
              <a
                key={item.conversation_id}
                href={`/chat/devis-ia?conversation=${encodeURIComponent(item.conversation_id)}`}
                className="block rounded-lg bg-black/30 px-2 py-1.5 hover:bg-black/50"
              >
                <p className="text-[11px] text-white font-medium truncate">
                  {item.client_name || 'Client'}
                  {item.devis_count ? ` · ${item.devis_count} brouillon(s)` : ' · escalade'}
                </p>
                <p className="text-[10px] text-gray-400 truncate">
                  {item.client_message || item.client_email || item.refs?.[0] || item.conversation_id}
                </p>
                {item.needs_staff_pricing && (
                  <p className="text-[10px] text-amber-300">Prix à saisir (cache fournisseur vide)</p>
                )}
              </a>
            ))}
          </div>
        )}
        {loading && messages.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6">Chargement du fil…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 px-4 space-y-2">
            <MessageSquare className="w-8 h-8 mx-auto text-purple-400/40" />
            <p className="text-xs text-gray-400">
              {isWorkspace
                ? 'Ex : « Écran iPhone 14 Pro » ou « Batterie Samsung A54 client Dupont 0694123456 »'
                : 'Posez une question : pièce alternative, formulation client, MO…'}
            </p>
            <p className="text-[10px] text-purple-300/80">
              Tapez « comparer écran iPhone 13 » pour le tableau Jens / Mobilax / LCD-Phone (prix achat, stock, modèle exact).
            </p>
            {clientMessage && (
              <p className="text-[10px] text-gray-600 italic line-clamp-3">« {clientMessage} »</p>
            )}
          </div>
        ) : (
          messages.map((m) => {
            const isBot = m.sender_role === 'ai' || m.is_ai_reply;
            return (
              <div key={m.id} className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                <div
                  className={`max-w-[92%] rounded-2xl px-3 py-2 text-xs whitespace-pre-wrap ${
                    isBot
                      ? 'bg-purple-500/15 text-gray-200 border border-purple-500/20 rounded-tl-sm'
                      : 'bg-cyan-600/30 text-white border border-cyan-500/20 rounded-tr-sm'
                  }`}
                >
                  {isBot && (
                    <div className="flex items-center gap-1 text-[10px] text-purple-300 mb-1 font-semibold">
                      <Bot className="w-3 h-3" /> {m.sender_name || 'Bot Auto-Devis'}
                    </div>
                  )}
                  {formatBotText(m.content)}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="p-3 border-t border-purple-500/20 bg-black/15">
        {isWorkspace && (
          <div className="mb-2" data-testid="staffbot-client-identity">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
              <input value={ident.name} onChange={(e) => setIdent((v) => ({ ...v, name: e.target.value }))} placeholder="Nom et prénom *" className="input-dark text-xs py-1.5" data-testid="staffbot-client-name" />
              <input value={ident.tel} onChange={(e) => setIdent((v) => ({ ...v, tel: e.target.value }))} placeholder="Téléphone *" className="input-dark text-xs py-1.5" inputMode="tel" data-testid="staffbot-client-tel" />
              <input value={ident.email} onChange={(e) => setIdent((v) => ({ ...v, email: e.target.value }))} placeholder="E-mail *" className="input-dark text-xs py-1.5" inputMode="email" data-testid="staffbot-client-email" />
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Nom, téléphone et e-mail sont obligatoires pour valider et envoyer le devis.</p>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            placeholder={
              isWorkspace
                ? 'Tapez « comparer » pour voir tous les fournisseurs — ou « Écran iPhone 13 Pro OLED — client Martin 0694… »'
                : 'Tapez « comparer » pour voir tous les fournisseurs, ou posez une question (pièce, MO, réponse client)'
            }
            className="flex-1 input-dark text-xs resize-none py-2"
            disabled={sending}
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !draft.trim()}
            className="self-end px-3 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {!isWorkspace && conversationId && (
          <a
            href={`/chat?conversation=${conversationId}`}
            className="inline-flex items-center gap-1 mt-2 text-[10px] text-gray-500 hover:text-gray-300"
          >
            <MessageSquare className="w-3 h-3" /> Ouvrir le chat client
          </a>
        )}
      </div>
    </div>
  );
};

export default AutoDevisStaffBotChat;
